import { FeatureTableOptions } from 'types'
import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import { GeckoWeld } from 'types/assetModel/features/welds/geckoWelds'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseString } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  optionalStringSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'
export interface GeckoGenericHeadRadialWeldJSON {
  name: string | undefined
  type: GeckoFeatureModelType.GenericHeadRadialWeld
  feature_geometry: {
    y_start: number
    y_end: number
    x: number
  }
  y_measurement_unit: string
}

export const NewGeckoGenericHeadRadialWeldJSON: GeckoGenericHeadRadialWeldJSON =
  {
    name: 'New Generic Head Radial Weld',
    type: GeckoFeatureModelType.GenericHeadRadialWeld,
    feature_geometry: {
      y_start: 0,
      y_end: 0,
      x: 0,
    },
    y_measurement_unit: 'meters',
  }

export class GeckoGenericHeadRadialWeld extends GeckoWeld {
  public readonly type = GeckoFeatureModelType.GenericHeadRadialWeld
  public readonly y_measurement_unit: string

  private constructor(
    componentID: string,
    weldID: string,
    weldJson: GeckoGenericHeadRadialWeldJSON
  ) {
    const name = parseString(weldJson.name)
    const feature_geometry = weldJson.feature_geometry as Record<string, number>
    super(
      componentID,
      weldID,
      name,
      feature_geometry.x as number,
      feature_geometry.y_start as number,
      feature_geometry.x as number,
      feature_geometry.y_end as number
    )
    this.y_measurement_unit = weldJson.y_measurement_unit
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJson: Record<string, GeckoGenericHeadRadialWeldJSON>
  ): Map<string, GeckoGenericHeadRadialWeld> {
    return Object.keys(recordsJson).reduce((resultObj, weldID) => {
      resultObj.set(
        weldID,
        new GeckoGenericHeadRadialWeld(componentID, weldID, recordsJson[weldID])
      )
      return resultObj
    }, new Map<string, GeckoGenericHeadRadialWeld>())
  }

  public static CreateFromJSON(
    componentID: string,
    weldID: string,
    weldJson: GeckoGenericHeadRadialWeldJSON
  ): GeckoGenericHeadRadialWeld {
    return new GeckoGenericHeadRadialWeld(componentID, weldID, weldJson)
  }

  public asJSON() {
    return generateGenericHeadRadialWeld(
      this.name,
      this.feature_geometry.x.min,
      this.feature_geometry.y.min,
      this.feature_geometry.y.max,
      this.y_measurement_unit
    )
  }
}

export function generateGenericHeadRadialWeld(
  name: string | undefined,
  x: GeckoMeasurement,
  yStart: GeckoMeasurement,
  yEnd: GeckoMeasurement,
  yMeasurementUnitString: string = 'meters'
): GeckoGenericHeadRadialWeldJSON {
  const yMeasurementUnit =
    yMeasurementUnitString === 'degrees'
      ? GeckoMeasurementUnit.Degrees
      : GeckoMeasurementUnit.Meters

  const GenericHeadRadialWeldJSON: GeckoGenericHeadRadialWeldJSON = {
    name: name ?? undefined,
    type: GeckoFeatureModelType.GenericHeadRadialWeld,
    feature_geometry: {
      x: x
        .convertTo(GeckoMeasurementUnit.Degrees)
        .toPrecision(assetModelPrecision).num,
      y_start: yStart
        .convertTo(yMeasurementUnit)
        .toPrecision(assetModelPrecision).num,
      y_end: yEnd.convertTo(yMeasurementUnit).toPrecision(assetModelPrecision)
        .num,
    },
    y_measurement_unit: yMeasurementUnitString,
  }

  return GenericHeadRadialWeldJSON
}

export const GenericHeadRadialWeldFormFields = (
  weldJSON: GeckoGenericHeadRadialWeldJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Weld Name',
      value: weldJSON.name,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'x',
      label: 'X Position',
      value: weldJSON.feature_geometry.x,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_start',
      label: 'Y Start',
      value: weldJSON.feature_geometry.y_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_end',
      label: 'Y End',
      value: weldJSON.feature_geometry.y_end,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_measurement_unit',
      label: 'Y Measurement Unit',
      value: weldJSON.y_measurement_unit,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
      options: [
        { value: 'degrees', label: 'Degrees' },
        { value: 'meters', label: 'Meters' },
      ],
    },
  ]
}

export const GenericHeadRadialWeldJSONFromFormFields = (
  formFields: FormField[]
) => {
  const selectedYUnitField = getFormFieldByDataKey(
    formFields,
    'y_measurement_unit'
  )?.value

  const selectedYUnit =
    selectedYUnitField === 'degrees'
      ? GeckoMeasurementUnit.Degrees
      : GeckoMeasurementUnit.Meters

  return generateGenericHeadRadialWeld(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x')?.value as number,
      GeckoMeasurementUnit.Degrees
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_start')?.value as number,
      selectedYUnit
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_end')?.value as number,
      selectedYUnit
    ),
    selectedYUnitField as string
  )
}

export const GenericHeadRadialWeldFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.GenericHeadRadialWeld,
  title: 'Radial Welds',
  singularName: 'Radial Weld',
  fields: ['x', 'y_start', 'y_end'],
  sortBy: [{ fieldName: 'y_start', order: 'asc' }],
  tableMessage:
    'Adding / Editing of Radial Welds is disabled as they are automatically generated from Plates and/or Courses',
}
